// Sign-in on the Lender Hub API (LH-B1): the same admin users as the old Hub admin. Steps as the API says:
// password → (the authenticator code, or a recovery code) → (a new password, first time) → (set up 2-step, when the
// Hub requires it) → in. The look follows App admin's sign-in (AuthLayout).
import { useCallback, useRef, useState, type FormEvent, type ReactNode } from 'react'
import { AlertCircle, ArrowLeft, Eye, EyeOff, KeyRound, Loader2, Lock, Mail, ShieldCheck, Smartphone } from 'lucide-react'
import { CopyButton, OTPInput } from '@/kit/ui'
import { ApiError, auth } from '@/lib/hubApi'
import { useSessionStore } from '@/store/sessionStore'
import { AuthLayout } from './AuthLayout'

const message = (err: unknown) => (err instanceof ApiError ? err.message : err instanceof Error ? err.message : String(err))

function ErrorLine({ error }: { error: string | null }) {
  if (!error) return null
  return (
    <p className="field-error flex items-center gap-1" role="alert">
      <AlertCircle size={12} className="flex-shrink-0" />
      {error}
    </p>
  )
}

function SubmitButton({ busy, disabled, busyText, children, testId }: { busy: boolean; disabled: boolean; busyText: string; children: ReactNode; testId?: string }) {
  return (
    <button type="submit" className="btn btn-primary flex w-full items-center justify-center gap-2" disabled={busy || disabled} data-testid={testId}>
      {busy ? <><Loader2 size={14} className="animate-spin" />{busyText}</> : children}
    </button>
  )
}

/** A password box with the lock icon and show / hide. */
function PasswordInput({ id, value, onChange, autoComplete, autoFocus }: { id: string; value: string; onChange: (v: string) => void; autoComplete: string; autoFocus?: boolean }) {
  const [shown, setShown] = useState(false)
  return (
    <div className="relative">
      <Lock size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
      <input id={id} className="input pl-8 pr-9" type={shown ? 'text' : 'password'} autoComplete={autoComplete} value={value} onChange={(e) => onChange(e.target.value)} autoFocus={autoFocus} />
      <button
        type="button"
        onClick={() => setShown((v) => !v)}
        className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
        aria-label={shown ? 'Hide password' : 'Show password'}
      >
        {shown ? <EyeOff size={14} /> : <Eye size={14} />}
      </button>
    </div>
  )
}

/** Who is signing in (the steps after the password). */
function SigningInAs({ name, email }: { name: string; email: string }) {
  return (
    <div className="flex items-start gap-2.5 rounded-lg border border-primary/30 bg-primary-soft p-3">
      <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded bg-primary text-white">
        <Smartphone size={14} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-xs font-semibold text-default">Signing in as {name}</p>
        <p className="truncate text-[11px] text-muted">{email}</p>
      </div>
    </div>
  )
}

export function SignInScreen() {
  const view = useSessionStore((s) => s.view)
  const accept = useSessionStore((s) => s.accept)
  const signOut = useSessionStore((s) => s.signOut)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [code, setCode] = useState('')
  const [useRecovery, setUseRecovery] = useState(false)
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [again, setAgain] = useState('')
  const [setup, setSetup] = useState<{ secret: string; otpauth_uri: string; qr_svg?: string } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const attempt = async (work: () => Promise<void>) => {
    setBusy(true)
    setError(null)
    try { await work() } catch (err) { setError(message(err)) } finally { setBusy(false) }
  }
  const run = (work: () => Promise<void>) => (e: FormEvent) => {
    e.preventDefault()
    void attempt(work)
  }

  // The 6-box code sends itself when full. OTPInput re-runs onComplete when the callback changes (kit gap K7), so the
  // callback stays the same and reads the latest state through a ref.
  const verifyCode = useRef<(c: string) => void>(() => {})
  verifyCode.current = (c: string) => {
    if (busy) return
    void attempt(async () => {
      try { accept(await auth.verify({ code: c })) } catch (err) { setCode(''); throw err }
    })
  }
  const onCodeComplete = useCallback((c: string) => verifyCode.current(c), [])
  const confirmCode = useRef<(c: string) => void>(() => {})
  confirmCode.current = (c: string) => {
    if (busy) return
    void attempt(async () => {
      try { accept(await auth.confirmTwoStep(c)) } catch (err) { setCode(''); throw err }
    })
  }
  const onConfirmComplete = useCallback((c: string) => confirmCode.current(c), [])

  const back = (
    <button type="button" className="mt-4 inline-flex items-center gap-1 text-2xs text-muted hover:text-default" onClick={() => void signOut()}>
      <ArrowLeft size={11} />
      Use another account
    </button>
  )

  if (!view) {
    return (
      <AuthLayout title="Sign in" subtitle="Lender Hub, for Firstfund staff">
        <form onSubmit={run(async () => accept(await auth.signIn(email.trim(), password)))} className="space-y-4">
          <div>
            <label className="label" htmlFor="si-email">Email</label>
            <div className="relative">
              <Mail size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input id="si-email" className="input pl-8" type="email" autoComplete="username" placeholder="you@firstfund.in" value={email} onChange={(e) => setEmail(e.target.value)} autoFocus />
            </div>
          </div>
          <div>
            <label className="label" htmlFor="si-password">Password</label>
            <PasswordInput id="si-password" value={password} onChange={setPassword} autoComplete="current-password" />
          </div>
          <ErrorLine error={error} />
          <SubmitButton busy={busy} disabled={!email || !password} busyText="Signing in…" testId="si-submit">Continue</SubmitButton>
          <p className="pt-2 text-center text-[11px] text-muted">Forgot your password? Ask a Hub admin to reset it.</p>
        </form>
      </AuthLayout>
    )
  }

  if (view.stage === 'mfa') {
    return (
      <AuthLayout title="2-step code" subtitle={useRecovery ? 'Enter one of the recovery codes you saved' : 'Enter the 6-digit code from your authenticator app'}>
        <form onSubmit={run(async () => accept(await auth.verify(useRecovery ? { recovery_code: code.trim() } : { code })))} className="space-y-4">
          <SigningInAs name={view.user.name} email={view.user.email} />
          {useRecovery ? (
            <div>
              <label className="label flex items-center gap-1.5" htmlFor="si-code"><KeyRound size={11} />Recovery code</label>
              <input id="si-code" className="input mono tracking-wider" autoComplete="off" value={code} onChange={(e) => setCode(e.target.value)} autoFocus />
              <p className="mt-1 text-[10px] text-muted">One of the codes you saved when you set up 2-step sign-in. Each works once.</p>
            </div>
          ) : (
            <div className="flex justify-center py-2">
              <OTPInput value={code} onChange={setCode} onComplete={onCodeComplete} error={!!error} disabled={busy} />
            </div>
          )}
          <ErrorLine error={error} />
          <SubmitButton busy={busy} disabled={useRecovery ? !code.trim() : code.length !== 6} busyText="Checking…">
            <ShieldCheck size={14} />Verify
          </SubmitButton>
          <button type="button" className="w-full text-center text-2xs text-primary-text underline" onClick={() => { setUseRecovery(!useRecovery); setCode(''); setError(null) }}>
            {useRecovery ? 'Use the app code' : 'Lost your phone? Use a recovery code'}
          </button>
        </form>
        {back}
      </AuthLayout>
    )
  }

  if (view.stage === 'change_password') {
    const mismatch = next && again && next !== again
    return (
      <AuthLayout title="Choose a new password" subtitle="First sign-in, or your password was reset. At least 12 characters.">
        <form onSubmit={run(async () => accept(await auth.changePassword(current, next)))} className="space-y-4">
          <SigningInAs name={view.user.name} email={view.user.email} />
          <div>
            <label className="label" htmlFor="si-current">Current (one-time) password</label>
            <PasswordInput id="si-current" value={current} onChange={setCurrent} autoComplete="current-password" autoFocus />
          </div>
          <div>
            <label className="label" htmlFor="si-new">New password</label>
            <PasswordInput id="si-new" value={next} onChange={setNext} autoComplete="new-password" />
            {next && next.length < 12 && <p className="mt-1 text-[10px] text-muted">{12 - next.length} more character{next.length === 11 ? '' : 's'} needed.</p>}
          </div>
          <div>
            <label className="label" htmlFor="si-again">New password again</label>
            <PasswordInput id="si-again" value={again} onChange={setAgain} autoComplete="new-password" />
            {mismatch && <p className="field-error">The two new passwords differ.</p>}
          </div>
          <ErrorLine error={error} />
          <SubmitButton busy={busy} disabled={!current || !next || next !== again} busyText="Saving…">Save the new password</SubmitButton>
        </form>
        {back}
      </AuthLayout>
    )
  }

  // mfa_setup: the Hub requires 2-step (ADMIN_TWO_STEP=required)
  return (
    <AuthLayout title="Set up 2-step sign-in" subtitle="This Hub asks for a code from an authenticator app at every sign-in.">
      {!setup ? (
        <form onSubmit={run(async () => setSetup(await auth.setUpTwoStep()))} className="space-y-4">
          <SigningInAs name={view.user.name} email={view.user.email} />
          <p className="text-xs text-muted">Have Google Authenticator, Microsoft Authenticator or a similar app on your phone.</p>
          <ErrorLine error={error} />
          <SubmitButton busy={busy} disabled={false} busyText="Starting…">Start</SubmitButton>
        </form>
      ) : (
        <form onSubmit={run(async () => accept(await auth.confirmTwoStep(code)))} className="space-y-4">
          <ol className="list-decimal space-y-1 pl-4 text-xs text-muted">
            <li>In the app: add an account and scan the QR code.</li>
            <li>Or enter the key by hand (type: time-based).</li>
            <li>Type the 6-digit code the app shows now.</li>
          </ol>
          {setup.qr_svg && <div className="mx-auto h-40 w-40 rounded-lg border border-slate-200 bg-white p-1" role="img" aria-label="QR code for the authenticator app" dangerouslySetInnerHTML={{ __html: setup.qr_svg }} />}
          <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 dark:border-slate-700 dark:bg-slate-900">
            <code className="mono flex-1 break-all text-xs">{setup.secret}</code>
            <CopyButton text={setup.secret} />
          </div>
          <div className="flex justify-center py-2">
            <OTPInput value={code} onChange={setCode} onComplete={onConfirmComplete} error={!!error} disabled={busy} />
          </div>
          <ErrorLine error={error} />
          <SubmitButton busy={busy} disabled={code.length !== 6} busyText="Turning on…">Turn on 2-step</SubmitButton>
        </form>
      )}
      {back}
    </AuthLayout>
  )
}
